import React, { FC, useEffect, useState } from 'react'
import { RouteComponentProps } from "@reach/router"
import _ from 'lodash'
import Isvg from 'react-inlinesvg'
// Components
import { Toolbar } from "components/subnav"
import MFRentRollTable from "components/multifamily/rent-roll/rent-roll-table"
import ImportIcon from "components/import-export/import-icon"
import ExportIcon from "components/import-export/export-icon"
import Import from "components/import-export/import"
import { WithTip } from 'components/tooltip/with-tip'
// Data
import api_match from "data/interface/api"
import Client from "data/_client"
import importService from "data/import"
import inflationService, { Inflation } from "data/inflation"
import multifamilyService, { Multifamily } from "data/multifamily"
import socketService, * as eventType from "data/socket"
import propertyService, { Property } from 'data/property'
import { localizeNum } from "data/lib/numbers.js"
import { ContextRow } from './rent-roll-table'
import toggles from "feature-toggles"
//Styles
import { ContentSummary_s } from "styles/app-styles"
import { RentRoll_s } from "./rent-roll-styles"
import { AddIcon } from "images/icons/tools"


type SummaryType = {
  totalArea: string
  totalAreaVariance: string
  totalCalculatedArea: string
  totalCalculatedUnits: number
  totalUnits: number
  totalUnitVariance: number,
  totalFutureUnits: number,
  totalFutureArea: number
}

const tableId = "mf-rent-roll-table"

const MFRentRoll: FC<RouteComponentProps> = (props) => {
  const [data, setData] = useState<Multifamily[]>([])
  const [inflation, setInflation] = useState<Inflation[]>([])
  const [showImport, toggleShowImport] = useState<boolean>(false)
  const [summary, setSummary] = useState<SummaryType>()
  const [property, setProperty] = useState<Property|null>(null);
  const [propertyId, setPropertyId] = useState(propertyService.getStoredPropertyId())
  const [showSelected, setShowSelected] = useState<boolean>(false)
  const [selected, setSelected] = useState<ContextRow[]>([]);

  useEffect(() => {
    importService.stream().subscribe(data => {
      toggleShowImport(data['showImport'])
    })

    toggleShowImport(false);

    const multifamilySubscription = multifamilyService.stream().subscribe(data => {
      api_match(
        data,
        init  => setData(init.value),
        ok    => {
          setData(ok.value)
        },
        err   => {
          setData(err.value)
        },
        fetch => {}
      )
    })

    const socketSubscription = socketService.stream().subscribe(data => {
      switch(data.eventType) {
        case eventType.RENTROLL_SUMMARY_COMPLETE:
          setSummary(_.get(data, 'event.msg.data.multifamily'))
          break;
      }
    })

    const inflationSubscription = inflationService.stream().subscribe(data => {
      api_match(
        data,
        init  => setInflation(init.value),
        ok    => {
          setInflation(ok.value)
        },
        err   => {
          setInflation(err.value) // backout any optimistic updates
        },
        fetch => {}
      )
    })

    const propertySubscription = propertyService.stream().subscribe((data) => {
      if (data.value.length > 0){
        setProperty(_.find(data.value, {propertyid: data.current}))
      }
      switch (data.state) {
        case 'ok':
          setPropertyId(data.current)
          break
      }
    })

    return function cleanup() {
      multifamilySubscription.unsubscribe()
      socketSubscription.unsubscribe()
      inflationSubscription.unsubscribe()
      propertySubscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (_.isNil(propertyId)) return

    multifamilyService.fetchByPropertyId(propertyId)
    inflationService.fetchInflation(propertyId)
    propertyService.fetchProperty(propertyId)

    Client.post('msg/svc_rentroll_summary', { propertyId, _synchronous: true })
      .then((d) => _.get(d, 'data.multifamily'))
      .then(setSummary)
  }, [propertyId])

  let summaryText
  if (summary) {
    const f = (v) => localizeNum(v) || '-'
    const { totalArea, totalAreaVariance, totalCalculatedArea, totalUnits, totalCalculatedUnits, totalUnitVariance, totalFutureUnits, totalFutureArea } = summary
    summaryText = `Area: Total: ${f(totalArea)} | Input: ${f(totalCalculatedArea)} | Variance: ${f(totalAreaVariance)} | Future Area: ${f(totalFutureArea)} — Units: Total: ${f(totalUnits)} | Input: ${f(totalCalculatedUnits)} | Variance: ${f(totalUnitVariance)} | Future Units: ${f(totalFutureUnits)}`
  }

  const Add = () => {
    return (
      <a onClick={() => multifamilyService.create(propertyService.getStoredPropertyId())}>
        <Isvg src={AddIcon} width="20px" />
      </a>
    )
  }

  const [deleteFlag, setDeleteFlag] = useState(0)
  const Delete = () => {
    return (
      <WithTip position="bottom">
        <span>Delete Selected Rows</span>
        <a className={selected.length && !_.some(selected, 'systeminput')  ? '' : 'disabled'}
          onClick={() => {
            if (!selected.length || _.some(selected, 'systeminput')) return
            setDeleteFlag(deleteFlag+1)
        }}>
          <span className="p-menuitem-icon pi pi-fw pi-trash"></span>
        </a>
      </WithTip>
    )
  }

  const [pasteFlag, setPasteFlag] = useState(0)
  const Paste = () => {
    return (
      <WithTip position="bottom">
        <span>Paste to Selected Rows</span>
        <a className={selected.length && !_.some(selected, 'systeminput') ? '' : 'disabled'}
          onClick={() => {
            if (!selected.length || _.some(selected, 'systeminput')) return
            setPasteFlag(pasteFlag+1)
          }
        }>
          <span className="p-menuitem-icon pi pi-fw pi-clone"></span>
        </a>
      </WithTip>
    )
  }

  const ShowSelected = () => (
    <WithTip position="bottom">
      <span>Show {showSelected ? 'All' : 'Selected'} Rows</span>
      <a className={selected.length ? '' : 'disabled'}
        onClick={() => {
          if (!selected.length) return
          setShowSelected(!showSelected)
        }}>
        <span className={showSelected ? 'pi pi-eye-slash' : 'pi pi-eye'} style={{height: '18px', width: '18px'}}></span>
      </a>
    </WithTip>
  )

  useEffect(() => {
    setSelected(_.intersectionBy(data, selected, 'multifamilyid'))
  }, [data])

  useEffect(() => {
    if (showSelected && _.isEmpty(selected)) setShowSelected(false)
  }, [selected])

  const _data = showSelected ? selected : data

  return (
    <RentRoll_s>
      <Toolbar>
        <Add />
        {toggles.multiselect() && <>
          <Paste />
          <Delete />
          <ShowSelected />
        </>}
        <ImportIcon />
        <ExportIcon template="multifamily" />
      </Toolbar>
      { showImport && <Import /> }
      { data && property && property['defaultsmultifamily'] && inflation && !showImport && <MFRentRollTable tableId={tableId} data={_data} inflationData={inflation} defaults={property['defaultsmultifamily']} selectedState={[selected, setSelected]} flags={{deleteFlag, pasteFlag}} /> }
      { !showImport && <ContentSummary_s>{ summaryText }</ContentSummary_s> }
    </RentRoll_s>
  )
}

export default MFRentRoll
